import { type JsonMessage, type Room, type Transport, asText, randomId } from './transport';

/**
 * Friends list (research 04 §5.6). A friendship is a pair of characters that
 * share a random key; each side joins the friendship's private room while
 * playing, which gives online presence (zone, level) and a channel for
 * whispers and party invites wherever the friend is. Requests are exchanged
 * through any room both players share (zone, party, guild).
 */

export const MAX_FRIENDS = 50;
const HEARTBEAT_MS = 10000;
const REQUEST_MS = 60000;

export interface FriendRecord {
  uid: string;
  name: string;
  classId: string;
  key: string;
}

export interface FriendPresence {
  peer: string | null;
  zone: string | null;
  level: number;
  seen: number;
}

export interface FriendsDeps {
  transport: Transport;
  self(): { uid: string; name: string; classId: string; level: number; zone: string | null };
  now(): number;
  changed(): void;
  notify(text: string): void;
  /** Persisted list for the current character. */
  load(): FriendRecord[];
  save(list: FriendRecord[]): void;
  /** Sends through a room shared with `peer` (for requests); false if unreachable. */
  sendShared(peer: string, msg: JsonMessage): boolean;
  /** Incoming request (UI shows accept/decline). */
  requested(rid: string, fromName: string): void;
  /** Messages the friends protocol does not use (whispers, party invites). */
  other(from: string, m: JsonMessage): void;
}

interface Link {
  friend: FriendRecord;
  room: Room | null;
  offs: (() => void)[];
  presence: FriendPresence;
}

export class FriendsManager {
  private links = new Map<string, Link>();
  private outgoing = new Map<string, { peer: string; name: string; until: number }>();
  private incoming = new Map<string, { peer: string; name: string; uid: string; classId: string; key: string; until: number }>();
  private lastBeat = 0;

  constructor(private d: FriendsDeps) {}

  list(): { friend: FriendRecord; presence: FriendPresence }[] {
    return [...this.links.values()].map((l) => ({ friend: l.friend, presence: l.presence }));
  }

  /** Joins every friendship room for the current character. */
  open(): void {
    this.close();
    for (const f of this.d.load()) this.link(f);
    this.d.changed();
  }

  close(): void {
    for (const l of this.links.values()) this.unlink(l);
    this.links.clear();
  }

  private link(f: FriendRecord): void {
    const l: Link = { friend: f, room: null, offs: [], presence: { peer: null, zone: null, level: 0, seen: 0 } };
    this.links.set(f.uid, l);
    void this.d.transport.join(`f:${f.key}`, { password: f.key }).then((room) => {
      if (this.links.get(f.uid) !== l) {
        void room.leave();
        return;
      }
      l.room = room;
      l.offs.push(
        room.onMessage((from, m) => this.onMessage(l, from, m)),
        room.onPeerJoin((peer) => this.beat(l, peer)),
        room.onPeerLeave((peer) => {
          if (l.presence.peer !== peer) return;
          l.presence.peer = null;
          this.d.notify(`${l.friend.name} went offline.`);
          this.d.changed();
        }),
      );
      this.beat(l);
    });
  }

  private unlink(l: Link): void {
    for (const off of l.offs) off();
    l.offs = [];
    void l.room?.leave();
    l.room = null;
  }

  private beat(l: Link, to?: string): void {
    const me = this.d.self();
    l.room?.send({ t: 'fp', uid: me.uid, name: me.name, cls: me.classId, lvl: me.level, zone: me.zone }, to);
  }

  /** Sends a friend request to a reachable player. */
  request(peer: string, name: string): boolean {
    if (this.links.size >= MAX_FRIENDS) {
      this.d.notify('Your friends list is full.');
      return false;
    }
    const rid = randomId(8);
    const me = this.d.self();
    if (!this.d.sendShared(peer, { t: 'freq', rid, uid: me.uid, name: me.name, cls: me.classId })) {
      this.d.notify(`${name} is out of reach.`);
      return false;
    }
    this.outgoing.set(rid, { peer, name, until: this.d.now() + REQUEST_MS });
    this.d.notify(`Friend request sent to ${name}.`);
    return true;
  }

  /** Handles friend request traffic arriving through shared rooms. */
  onShared(from: string, m: JsonMessage): boolean {
    const now = this.d.now();
    if (m.t === 'freq') {
      const rid = asText(m.rid);
      const uid = asText(m.uid);
      if (!rid || !uid || this.links.has(uid)) return true;
      const name = asText(m.name, '?', 24);
      this.incoming.set(rid, { peer: from, name, uid, classId: asText(m.cls), key: '', until: now + REQUEST_MS });
      this.d.requested(rid, name);
      return true;
    }
    if (m.t === 'facc') {
      // The other side accepted: they chose the friendship key.
      const o = this.outgoing.get(asText(m.rid));
      const key = asText(m.key, '', 64);
      const uid = asText(m.uid);
      if (!o || o.peer !== from || now > o.until || !key || !uid) return true;
      this.outgoing.delete(asText(m.rid));
      this.add({ uid, name: asText(m.name, o.name, 24), classId: asText(m.cls), key });
      this.d.notify(`${o.name} accepted your friend request.`);
      return true;
    }
    if (m.t === 'fno') {
      const o = this.outgoing.get(asText(m.rid));
      if (o && o.peer === from) {
        this.outgoing.delete(asText(m.rid));
        this.d.notify(`${o.name} declined your friend request.`);
      }
      return true;
    }
    return false;
  }

  answer(rid: string, ok: boolean): void {
    const r = this.incoming.get(rid);
    this.incoming.delete(rid);
    if (!r || this.d.now() > r.until) return;
    if (!ok) {
      this.d.sendShared(r.peer, { t: 'fno', rid });
      return;
    }
    const key = randomId(20);
    const me = this.d.self();
    if (!this.d.sendShared(r.peer, { t: 'facc', rid, key, uid: me.uid, name: me.name, cls: me.classId })) return;
    this.add({ uid: r.uid, name: r.name, classId: r.classId, key });
  }

  private add(f: FriendRecord): void {
    if (this.links.has(f.uid)) return;
    const list = [...this.d.load().filter((x) => x.uid !== f.uid), f];
    this.d.save(list);
    this.link(f);
    this.d.changed();
  }

  remove(uid: string): void {
    const l = this.links.get(uid);
    if (!l) return;
    l.room?.send({ t: 'fdel' });
    this.unlink(l);
    this.links.delete(uid);
    this.d.save(this.d.load().filter((x) => x.uid !== uid));
    this.d.changed();
  }

  /** The friend's current peer id, when online. */
  peerOf(uid: string): string | null {
    return this.links.get(uid)?.presence.peer ?? null;
  }

  /** Sends to an online friend through the friendship room. */
  sendTo(peer: string, msg: JsonMessage): boolean {
    for (const l of this.links.values()) {
      if (l.presence.peer === peer && l.room) {
        l.room.send(msg, peer);
        return true;
      }
    }
    return false;
  }

  findByName(name: string): { peer: string; name: string } | null {
    const n = name.toLowerCase();
    for (const l of this.links.values()) if (l.presence.peer && l.friend.name.toLowerCase() === n) return { peer: l.presence.peer, name: l.friend.name };
    return null;
  }

  private onMessage(l: Link, from: string, m: JsonMessage): void {
    if (m.t === 'fp') {
      // Only the friend (by uid) may speak in the friendship room.
      if (asText(m.uid) !== l.friend.uid) return;
      const wasOnline = l.presence.peer !== null;
      l.presence = { peer: from, zone: typeof m.zone === 'string' ? m.zone : null, level: Number(m.lvl) || 0, seen: this.d.now() };
      const name = asText(m.name, l.friend.name, 24);
      if (name !== l.friend.name) {
        l.friend = { ...l.friend, name };
        this.d.save(this.d.load().map((x) => (x.uid === l.friend.uid ? l.friend : x)));
      }
      if (!wasOnline) this.d.notify(`${l.friend.name} is online.`);
      this.d.changed();
      return;
    }
    if (l.presence.peer !== from) return;
    if (m.t === 'fdel') {
      this.unlink(l);
      this.links.delete(l.friend.uid);
      this.d.save(this.d.load().filter((x) => x.uid !== l.friend.uid));
      this.d.changed();
      return;
    }
    this.d.other(from, m);
  }

  tick(): void {
    const now = this.d.now();
    if (now - this.lastBeat >= HEARTBEAT_MS) {
      this.lastBeat = now;
      for (const l of this.links.values()) this.beat(l);
    }
    for (const [rid, o] of this.outgoing) if (now > o.until) this.outgoing.delete(rid);
    for (const [rid, r] of this.incoming) if (now > r.until) this.incoming.delete(rid);
  }
}
